import { useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

const ECTO = '94,234,212';
const WRAITH = '139,124,246';
const MOTE = '165,243,252';
const FLEE_RADIUS = 200;

const STATIC_SVG = encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="220" height="220">' +
    '<filter id="n"><feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch"/></filter>' +
    '<rect width="100%" height="100%" filter="url(#n)"/></svg>',
);

const FOG_A = [
  'radial-gradient(ellipse at 20% 30%, rgba(94,234,212,.07), transparent 60%)',
  'radial-gradient(ellipse at 80% 60%, rgba(139,124,246,.08), transparent 60%)',
  'radial-gradient(ellipse at 50% 90%, rgba(94,234,212,.05), transparent 60%)',
].join(', ');

const FOG_B = [
  'radial-gradient(ellipse at 70% 20%, rgba(139,124,246,.07), transparent 60%)',
  'radial-gradient(ellipse at 30% 75%, rgba(94,234,212,.06), transparent 60%)',
].join(', ');

const DRIFT = {
  x: ['-4%', '3%', '5%'],
  y: ['2%', '-3%', '3%'],
  scale: [1, 1.08, 1.02],
};

const isDark = () => document.documentElement.classList.contains('dark');

function spawn(w, h, initial = false) {
  const r = 12 + Math.random() * 30;
  return {
    x: Math.random() * w,
    y: initial ? Math.random() * h : h + r * 2,
    vx: (Math.random() - 0.5) * 0.25,
    vy: -(0.12 + Math.random() * 0.3),
    r,
    phase: Math.random() * Math.PI * 2,
    speed: 0.6 + Math.random() * 0.8,
    col: Math.random() > 0.4 ? ECTO : WRAITH,
    alpha: 0,
    life: initial ? 120 + Math.random() * 480 : 0,
    maxLife: 900 + Math.random() * 900,
  };
}

function drawGhost(ctx, g, t) {
  const { x, y, r, col } = g;
  const a = g.alpha;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(Math.sin(t * 0.001 * g.speed + g.phase) * 0.12 + g.vx * 0.8);

  const aura = ctx.createRadialGradient(0, 0, r * 0.2, 0, 0, r * 2.4);
  aura.addColorStop(0, `rgba(${col},${0.16 * a})`);
  aura.addColorStop(1, `rgba(${col},0)`);
  ctx.fillStyle = aura;
  ctx.beginPath();
  ctx.arc(0, 0, r * 2.4, 0, Math.PI * 2);
  ctx.fill();

  const body = ctx.createLinearGradient(0, -r, 0, r * 1.6);
  body.addColorStop(0, `rgba(${col},${0.42 * a})`);
  body.addColorStop(0.6, `rgba(${col},${0.16 * a})`);
  body.addColorStop(1, `rgba(${col},0)`);
  ctx.fillStyle = body;
  ctx.strokeStyle = `rgba(${col},${0.5 * a})`;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(-r, 0);
  ctx.arc(0, 0, r, Math.PI, 0);
  const waves = 4;
  const tail = r * 1.6;
  for (let i = 0; i <= waves * 2; i += 1) {
    const px = r - (i / (waves * 2)) * r * 2;
    const wob = Math.sin(t * 0.004 * g.speed + i + g.phase) * r * 0.18;
    const py = tail + (i % 2 === 0 ? 0 : -r * 0.35) + wob;
    ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  const blink = Math.sin(t * 0.002 + g.phase * 3) > 0.97 ? 0.15 : 1;
  ctx.fillStyle = `rgba(0,0,0,${0.75 * a})`;
  ctx.beginPath();
  ctx.ellipse(-r * 0.33, -r * 0.1, r * 0.13, r * 0.2 * blink, 0, 0, Math.PI * 2);
  ctx.ellipse(r * 0.33, -r * 0.1, r * 0.13, r * 0.2 * blink, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(0, r * 0.38, r * 0.11, r * 0.17 + Math.sin(t * 0.003 + g.phase) * r * 0.04, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** Fixed haunted layer behind public pages: drifting ghosts that flee the pointer, motes, fog and static. */
export default function HauntBackground() {
  const ref = useRef(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return undefined;
    let w = 0;
    let h = 0;
    let raf = 0;
    const mouse = { x: -9999, y: -9999 };
    let scrollY = window.scrollY;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const small = w < 700;
    const ghosts = Array.from({ length: small ? 4 : 7 }, () => spawn(w, h, true));
    const motes = Array.from({ length: small ? 30 : 70 }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vy: -(0.05 + Math.random() * 0.25),
      r: Math.random() * 1.4 + 0.3,
      a: Math.random() * 0.6 + 0.1,
      tw: Math.random() * Math.PI * 2,
    }));

    const tick = (t) => {
      ctx.clearRect(0, 0, w, h);
      const parallax = scrollY * 0.15;
      const tone = isDark() ? 1 : 0.5;

      for (const m of motes) {
        m.y += m.vy;
        m.tw += 0.03;
        if (m.y < -5) {
          m.y = h + 5;
          m.x = Math.random() * w;
        }
        const y = (((m.y - parallax * 0.5) % h) + h) % h;
        ctx.fillStyle = `rgba(${MOTE},${m.a * (0.5 + 0.5 * Math.sin(m.tw)) * tone})`;
        ctx.beginPath();
        ctx.arc(m.x, y, m.r, 0, Math.PI * 2);
        ctx.fill();
      }

      for (let i = 0; i < ghosts.length; i += 1) {
        const g = ghosts[i];
        g.life += 1;
        const fadeIn = Math.min(1, g.life / 120);
        const fadeOut = Math.min(1, (g.maxLife - g.life) / 160);
        const flick = Math.random() > 0.995 ? 0.2 : 1;
        const pulse = 0.28 + 0.22 * Math.sin(t * 0.0008 * g.speed + g.phase) ** 2;
        g.alpha = Math.max(0, Math.min(fadeIn, fadeOut)) * flick * pulse * tone;

        const dx = g.x - mouse.x;
        const dy = g.y - mouse.y;
        const d = Math.hypot(dx, dy);
        if (d < FLEE_RADIUS && d > 0.1) {
          const f = (1 - d / FLEE_RADIUS) * 0.35;
          g.vx += (dx / d) * f;
          g.vy += (dy / d) * f;
        }
        g.vx *= 0.97;
        g.vy = g.vy * 0.97 - 0.15 * g.speed * 0.03;
        g.x += g.vx + Math.sin(t * 0.0006 * g.speed + g.phase) * 0.35;
        g.y += g.vy;

        if (g.x < -100) g.x = w + 80;
        if (g.x > w + 100) g.x = -80;
        if (g.life > g.maxLife || g.y < -150) ghosts[i] = spawn(w, h);
        drawGhost(ctx, ghosts[i], t);
      }
      if (!reduce) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const onMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };
    const onLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };
    const onScroll = () => {
      scrollY = window.scrollY;
    };
    window.addEventListener('resize', resize);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerleave', onLeave);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerleave', onLeave);
      window.removeEventListener('scroll', onScroll);
    };
  }, [reduce]);

  const fogMotion = (duration, direction) =>
    reduce ? {} : { animate: DRIFT, transition: { duration, ease: 'easeInOut', repeat: Infinity, repeatType: direction } };

  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
      <motion.div className="absolute" style={{ inset: '-20%', background: FOG_A, opacity: 0.55 }} {...fogMotion(38, 'mirror')} />
      <motion.div className="absolute" style={{ inset: '-20%', background: FOG_B, opacity: 0.4 }} {...fogMotion(55, 'reverse')} />
      <canvas ref={ref} className="absolute inset-0" />
      <div
        className="absolute inset-0 opacity-[0.03] dark:opacity-[0.06]"
        style={{ backgroundImage: `url("data:image/svg+xml,${STATIC_SVG}")`, backgroundSize: '220px 220px', mixBlendMode: 'screen' }}
      />
      {!reduce && (
        <motion.div
          className="absolute inset-x-0"
          style={{ height: 140, background: 'linear-gradient(to bottom, transparent, rgba(94,234,212,.035), transparent)' }}
          initial={{ top: '-140px' }}
          animate={{ top: '100%' }}
          transition={{ duration: 9, ease: 'linear', repeat: Infinity }}
        />
      )}
    </div>
  );
}
